import isEmail from 'validator/lib/isEmail';
import normalizeEmailLib from 'validator/lib/normalizeEmail';
import Mailcheck from 'mailcheck';
import disposableDomains from 'disposable-email-domains';

// O(1) lookup; the JSON ships ~3000 entries so a Set avoids per-keystroke
// linear scans on the typo/blur paths.
const disposableSet = new Set<string>(disposableDomains);

// Adds Gulf/MENA/India + corporate domains to mailcheck's default list so
// suggestions favor the domains our users actually type.
const TYPO_DOMAINS = [
  // mailcheck defaults — keep them so global users still get gmail/yahoo etc.
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'aol.com', 'live.com', 'msn.com',
  // Gulf / MENA / India
  'etisalat.ae', 'du.ae', 'emirates.net.ae',
  'mail.ru', 'yandex.ru',
  'rediffmail.com',
  // Corporate
  'smarteco.com',
  'ispatialtec.com',
];

const MAX_EMAIL_LENGTH = 254; // RFC 5321

// Free-mail providers where we apply extra spam-pattern heuristics. Corporate
// and carrier domains often issue number-only handles legitimately, so we
// scope this check narrowly to avoid false positives.
const FREE_MAIL_PROVIDERS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'aol.com', 'live.com', 'msn.com',
]);

// Catches obvious throwaway patterns the format validator can't: an all-digit
// local part (e.g. 234234234@gmail.com) or a single character repeated
// (e.g. aaaaaa@gmail.com, 11111@gmail.com).
const looksLikeFakeLocalPart = (localPart: string): boolean => {
  if (/^\d+$/.test(localPart)) return true;
  if (/^(.)\1+$/.test(localPart)) return true;
  return false;
};

export type EmailValidationResult = {
  ok: boolean;
  // Hard error — caller should block submit and show this string.
  error?: string;
  // Soft hint — caller should render a clickable "Did you mean …?" suggestion.
  suggestion?: string;
};

export type EmailValidationOptions = {
  // Default true. Set false on Login / ResetPassword if disposable accounts
  // may exist in the legacy data and a hard block would lock them out.
  blockDisposable?: boolean;
  // Default true on Register/Settings, false on Forgot Password / Verify
  // resend (avoid leaking domain hints to anonymous flows).
  checkTypo?: boolean;
};

// Trim + lowercase. Kept separate so callers can decide whether to mutate
// the input value or just validate against a normalized copy.
export const normalizeEmail = (email: string): string => {
  if (!email) return '';
  return email.trim().toLowerCase();
};

// `validator.normalizeEmail` does provider-specific normalization
// (gmail dots, plus-tags, etc.) — we use it ONLY for the disposable lookup so
// `user+anything@gmail.com` and `User@Gmail.com` resolve to the same domain.
const domainOf = (email: string): string => {
  const at = email.lastIndexOf('@');
  return at >= 0 ? email.slice(at + 1).toLowerCase() : '';
};

const isDisposable = (email: string): boolean => {
  const canonical = normalizeEmailLib(email, { all_lowercase: true }) || email;
  return disposableSet.has(domainOf(canonical));
};

const findTypoSuggestion = (email: string): string | undefined => {
  let suggested: string | undefined;
  Mailcheck.run({
    email,
    domains: TYPO_DOMAINS,
    suggested: (s: { full: string }) => {
      suggested = s.full;
    },
  });
  return suggested;
};

export const validateEmail = (
  rawEmail: string | null | undefined,
  options: EmailValidationOptions = {},
): EmailValidationResult => {
  const { blockDisposable = true, checkTypo = true } = options;
  const email = normalizeEmail(rawEmail || '');

  if (!email) {
    return { ok: false, error: 'Email is required' };
  }

  if (email.length > MAX_EMAIL_LENGTH) {
    return { ok: false, error: 'Email must be less than 254 characters' };
  }

  // `allow_utf8_local_part: false` keeps things conservative for backend
  // compatibility; flip later if international addresses become a need.
  if (!isEmail(email, { allow_utf8_local_part: false })) {
    return { ok: false, error: 'Please enter a valid email address' };
  }

  const atIndex = email.lastIndexOf('@');
  const localPart = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);

  // --- Gibberish / invalid domain checks ---

  // TLD must be 2–6 characters (covers .com, .org, .travel, .museum etc.)
  const tldMatch = domain.match(/\.([a-z]+)$/);
  if (!tldMatch || tldMatch[1].length < 2 || tldMatch[1].length > 6) {
    return { ok: false, error: 'Please enter a valid email address' };
  }

  // Each domain label max 63 chars (RFC 1035), min 1 char
  const domainLabels = domain.split('.');
  if (domainLabels.some(label => label.length > 63 || label.length === 0)) {
    return { ok: false, error: 'Please enter a valid email address' };
  }

  // Domain name (without TLD) should not be excessively long — real domains rarely exceed 20 chars
  const domainName = domainLabels.slice(0, -1).join('.');
  if (domainName.length > 30) {
    return { ok: false, error: 'Please enter a valid email address' };
  }

  // Detect gibberish: too many consecutive consonants (5+) in domain or local part
  const hasGibberish = (str: string): boolean => /[^aeiou0-9@._-]{5,}/i.test(str);
  if (hasGibberish(domainName) || hasGibberish(localPart)) {
    return { ok: false, error: 'Please enter a valid email address' };
  }

  if (FREE_MAIL_PROVIDERS.has(domain) && looksLikeFakeLocalPart(localPart)) {
    return { ok: false, error: 'Please enter a valid email address' };
  }

  if (blockDisposable && isDisposable(email)) {
    return { ok: false, error: 'Please use a permanent email address' };
  }

  // Valid so far. Attach a typo suggestion if mailcheck has one — it's a
  // soft hint, not an error, so we still return ok: true.
  if (checkTypo) {
    const suggestion = findTypoSuggestion(email);
    if (suggestion && suggestion !== email) {
      return { ok: true, suggestion };
    }
  }

  return { ok: true };
};
